/**
 * Запросы раздела «Проекты» (US-05.1/US-05.2) — `/api/public/projects/*`.
 *
 * Эндпоинты `/public/projects/infrastructure` и `/public/projects/investment`
 * на backend ещё НЕ реализованы: контракты (snake_case, параметр `kato`;
 * у инфраструктурных — ещё опциональный `year`, как у `/public/analytics/*`)
 * заложены здесь заранее; до появления рестов запрос для не-демо КАТО
 * отклоняется 404 — вызывающий код деградирует без ошибки интерфейса
 * (виджеты скрываются, реестр показывается пустым, FR-3.5/FR-4.1 US-05.1,
 * FR-2.5 US-05.2).
 *
 * Ответы прогоняются через нормализаторы: пустое тело / отсутствующие поля /
 * мусор превращаются в безопасные значения (`null` / `[]`), формат —
 * view-модели из `types/projects.ts` (общие с моками
 * `hardData/projectsInfrastructure.json` / `projectsInvestment.json`).
 */
import { api } from './api';
import type {
  AbpSlice,
  InfrastructureProject,
  InfrastructureProjectsData,
  InvestmentProject,
  InvestmentProjectsData,
  LocalizedText,
  ProcurementSlice,
  ProjectContract,
  ProjectsSlice,
} from '../types/projects';

// --- примитивы нормализации (как в analytics.request.ts) ---

const asObj = (v: unknown): Record<string, unknown> =>
  typeof v === 'object' && v !== null ? (v as Record<string, unknown>) : {};

const asArr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);

const num = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null;

const str = (v: unknown): string | null =>
  typeof v === 'string' && v.length > 0 ? v : null;

/** «2025-03-01» / «2025-03» → «03.2025» (сроки реестра, FR-3.1). */
const monthYear = (v: unknown): string | null => {
  const s = str(v);
  if (s === null) return null;
  const m = /^(\d{4})-(\d{2})/.exec(s);
  return m ? `${m[2]}.${m[1]}` : s;
};

const periodFrom = (start: unknown, end: unknown): string | null => {
  const s = monthYear(start);
  const e = monthYear(end);
  if (s === null && e === null) return null;
  return `${s ?? '…'} — ${e ?? '…'}`;
};

const sliceFrom = (entry: unknown): ProjectsSlice[] => {
  const e = asObj(entry);
  const key = str(e.key);
  if (key === null) return [];
  return [
    {
      key,
      planSumMln: num(e.plan_sum_mln),
      factSumMln: num(e.fact_sum_mln),
      planCount: num(e.plan_count),
      factCount: num(e.fact_count),
    },
  ];
};

const contractFrom = (entry: unknown): ProjectContract[] => {
  const e = asObj(entry);
  const name = str(e.name);
  if (name === null) return [];
  return [
    {
      type: str(e.type) ?? '',
      name,
      sumMln: num(e.sum_mln),
      procurement: str(e.procurement),
      developmentPct: num(e.development_pct),
    },
  ];
};

const projectFrom = (entry: unknown, index: number): InfrastructureProject[] => {
  const e = asObj(entry);
  const name = str(e.name);
  // Строка реестра без названия проекта — нечего показывать.
  if (name === null) return [];
  return [
    {
      id: num(e.id) ?? index,
      name,
      territory: str(e.territory),
      work: str(e.work) ?? '',
      sumMln: num(e.sum_mln),
      period: str(e.period) ?? periodFrom(e.date_start, e.date_end),
      abp: str(e.abp),
      procurement: str(e.procurement),
      status: str(e.status),
      planYearMln: num(e.plan_year_mln),
      factYearMln: num(e.fact_year_mln),
      factTotalMln: num(e.fact_total_mln),
      budgetClassification: str(e.budget_classification),
      hasPrimaryData: e.has_primary_data === true,
      contracts: asArr(e.contracts).flatMap(contractFrom),
    },
  ];
};

export const normalizeInfrastructureProjects = (
  raw: unknown,
): InfrastructureProjectsData => {
  const root = asObj(raw);
  const plashka = asObj(root.plashka);
  return {
    year: num(root.year),
    plashka: {
      projectsCount: num(plashka.projects_count),
      contractsCount: num(plashka.contracts_count),
      completedCount: num(plashka.completed_count),
      planTotalMln: num(plashka.plan_total_mln),
      yearDevelopmentPct: num(plashka.year_development_pct),
      remainMln: num(plashka.remain_mln),
      tenderSharePct: num(plashka.tender_share_pct),
      tenderContractsCount: num(plashka.tender_contracts_count),
    },
    works: asArr(root.works).flatMap(sliceFrom),
    industries: asArr(root.industries).flatMap(sliceFrom),
    procurement: asArr(root.procurement).flatMap((entry) => {
      const e = asObj(entry);
      const key = str(e.key);
      if (key === null) return [];
      const slice: ProcurementSlice = {
        key,
        planSumMln: num(e.plan_sum_mln),
        factSumMln: num(e.fact_sum_mln),
        contractsCount: num(e.contracts_count),
      };
      return [slice];
    }),
    abp: asArr(root.abp).flatMap((entry) => {
      const e = asObj(entry);
      const name = str(e.name);
      if (name === null) return [];
      const slice: AbpSlice = {
        name,
        planMln: num(e.plan_mln),
        factMln: num(e.fact_mln),
        contractsCount: num(e.contracts_count),
      };
      return [slice];
    }),
    projects: asArr(root.projects).flatMap(projectFrom),
  };
};

export const getProjectsInfrastructure = async (
  kato: number,
  year?: number | null,
): Promise<InfrastructureProjectsData> => {
  const params: Record<string, number> = { kato };
  if (year != null) {
    params.year = year;
  }
  const response = await api.get<unknown>('/public/projects/infrastructure', {
    params,
  });
  return normalizeInfrastructureProjects(response.data);
};

// --- Инвестиционные проекты (US-05.2) ---

/** `*_ru` / `*_kz` ответа → LocalizedText; обе версии пустые → null. */
const localized = (
  obj: Record<string, unknown>,
  field: string,
): LocalizedText | null => {
  const ru = str(obj[`${field}_ru`]);
  const kz = str(obj[`${field}_kz`]);
  if (ru === null && kz === null) return null;
  return { ru, kz };
};

const investmentProjectFrom = (
  entry: unknown,
  index: number,
): InvestmentProject[] => {
  const e = asObj(entry);
  const name = localized(e, 'name');
  // Проект без названия — нечего показывать в аккордеоне (FR-2.1).
  if (name === null) return [];
  return [
    {
      id: num(e.id) ?? index,
      name,
      year: num(e.year),
      capacity: localized(e, 'capacity'),
      investmentMln: num(e.investment_mln),
      jobs: num(e.jobs),
      description: localized(e, 'description'),
      effect: localized(e, 'effect'),
      extraFields: asArr(e.extra_fields).flatMap((field) => {
        const f = asObj(field);
        const label = localized(f, 'label');
        const value = localized(f, 'value');
        if (label === null || value === null) return [];
        return [{ label, value }];
      }),
      photos: asArr(e.photos).flatMap((photo) => str(photo) ?? []),
    },
  ];
};

export const normalizeInvestmentProjects = (
  raw: unknown,
): InvestmentProjectsData => {
  const root = asObj(raw);
  return {
    projects: asArr(root.projects).flatMap(investmentProjectFrom),
  };
};

/** Реестр не привязан к отчётному году (FR-6.1) — параметр только `kato`. */
export const getProjectsInvestment = async (
  kato: number,
): Promise<InvestmentProjectsData> => {
  const response = await api.get<unknown>('/public/projects/investment', {
    params: { kato },
  });
  return normalizeInvestmentProjects(response.data);
};
